import type {RequestFilter, RequestFilterData} from './types';
import type {AuthenticationStore} from '../authentication/AuthenticationStore';
import {authStore} from '../authentication/AuthenticationStore';

/**
 * Request filter for V2 payout method GraphQL operations.
 *
 * This overrides the Authorization header to use the external account ephemeral key
 * (used by the external account form).
 *
 * The external account ephemeral key is required for these operations instead of the
 * standard account session key. This pattern is inspired by how we override the auth
 * header for V1 external account requests in `addExternalAccountFilter.ts`.
 */
export const addPayoutMethodFilter: RequestFilter = {
  matchers: [
    // Create a v2 payout method
    {
      urlPath: /\/graphql\/multiregion/,
      method: 'POST',
      body: /CreateV2OutboundSetupIntentMutation/,
    },
    // Archive a v2 payout method
    {
      urlPath: /\/graphql\/multiregion/,
      method: 'POST',
      body: /ArchiveV2PayoutMethodMutation/,
    },
    // Create an outbound destination from a financial connections session
    {
      urlPath: /\/graphql\/multiregion/,
      method: 'POST',
      body: /CreateV2OutboundDestinationFromFinancialConnectionsResultMutation/,
    },
    // Update balance settings, specifically default payout method for a currency
    {
      urlPath: /\/v1\/balance_settings/,
      method: 'POST',
      body: /default_payout_method_for_currency/,
    },

    // READ ENDPOINTS
    {
      urlPath: /\/graphql\/multiregion/,
      method: 'POST',
      body: /ListV2PayoutMethodsQuery/,
    },
  ],

  filterRequest: (requestData) => {
    if (
      requestData.request.body?.toString().includes('ListV2PayoutMethodsQuery')
    ) {
      // ListV2PayoutMethodsQuery is a special case that were are (temporarily) adding a sentinal header to.
      return addSentinalHeader(requestData);
    }

    // All other intercepts are overridding the authorization header to use the external account key
    return addAuthorizationHeader(requestData, authStore);
  },
};

function addSentinalHeader(requestData: RequestFilterData): RequestFilterData {
  return {
    ...requestData,
    request: {
      ...requestData.request,
      headers: {
        ...requestData.request.headers,
        'x-stripe-sentinal-value': `payout-methods-request`,
      },
    },
  };
}

function addAuthorizationHeader(
  requestData: RequestFilterData,
  authStore: AuthenticationStore,
): RequestFilterData {
  const selectedApiKeyAndType = authStore.getApiKey('external_account');
  if (!selectedApiKeyAndType) {
    throw new Error('User has not stored external_account key');
  }

  return {
    ...requestData,
    request: {
      ...requestData.request,
      headers: {
        ...requestData.request.headers,
        Authorization: `Bearer ${selectedApiKeyAndType.key}`,
      },
    },
  };
}
